import React, { useEffect } from "react";
import { Box, Typography, Card, CardContent } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getNextPlan, getSubscriptionPlans } from "selectors/paymentSelectors";
import {
  fetchBillingHistory,
  fetchSubscriptionPlans,
  updateSubscriptionPlanAction,
} from "actions/paymentActions";
import RenderDataFields from "components/DataFields/RenderDataFields";
import moment from "moment";
import HiddenOn from "components/Wrappers/HiddenOn";
import { InfoOutlined } from "@mui/icons-material";
import { ModelDialog } from "components/Model";
import { get } from "lodash";

export const MySubscriptions = ({ userData }) => {
  const dispatch = useDispatch();
  const subscriptionPlans = useSelector(getSubscriptionPlans);
  const nextSubscriptionPlan = useSelector(getNextPlan);

  const [planSelected, setPlanSelected] = React.useState({});
  const [showConfirm, setShowConfirm] = React.useState(false);

  useEffect(() => {
    dispatch(fetchSubscriptionPlans());
    dispatch(fetchBillingHistory({ nextSubscription: true }));
  }, [dispatch]);

  const renderPlansData = [
    {
      key: "planName",
      label: "Plan",
      dataPath: "subscriptionPlan",
      width: "45%",
      formatter: (value) => {
        return <Typography className="h3">{value}</Typography>;
      },
    },
    {
      key: "actualPrice",
      label: "Actual Price",
      dataPath: "price",
      width: "45%",
      formatter: (_, data) => {
        return (
          <>
            <Typography className="body1">
              <span
                className="ms-2"
                style={{
                  textDecoration: "line-through",
                  fontWeight: "600",
                }}
              >
                $ {data.price}
              </span>
            </Typography>
          </>
        );
      },
    },
    {
      key: "discPrice",
      label: "Discount Price",
      dataPath: "discPrice",
      width: "100%",

      formatter: (_, data) => {
        return (
          <>
            <Typography className="h3">
              <span
                className="ms-2"
                style={{ fontWeight: "600", color: "green" }}
              >
                ${data.discPrice}
              </span>
              <span
                className="ms-2"
                style={{ fontSize: "12px", color: "green" }}
              >
                ({data.discountPct}% Off)
              </span>
            </Typography>
          </>
        );
      },
    },
  ];

  const isSamePlan = (planId) => planId === planSelected.subscriptionId;

  const upgradePlanSelection = () => {
    const onSuccess = () => {
      setPlanSelected({});
    };
    dispatch(
      updateSubscriptionPlanAction(planSelected.subscriptionId, onSuccess)
    );
  };

  const filteredPlans = subscriptionPlans.filter(
    (plan) =>
      plan.subscriptionId !== userData.subscriptionId &&
      plan.subscriptionId !== nextSubscriptionPlan.subscriptionId
  );

  const upcomingPlanFilter = subscriptionPlans.filter(
    (plan) => plan.subscriptionId === nextSubscriptionPlan.subscriptionId
  );

  const PlanConfirmationDialog = () => {
    return (
      <>
        <div>
          <Typography className="h5 mb-4" style={{ fontWeight: 500 }}>
            You already have a new subscription plan scheduled for
            <span style={{ fontWeight: 700 }}>
              <br />
              {moment(nextSubscriptionPlan.startDate).format("LL")}
            </span>
            . Are you sure you want to upgrade your plan again?
          </Typography>
        </div>
        <div className="mt-4 text-center d-flex justify-content-center">
          <button
            className="secondaryButton"
            style={{ width: "6rem" }}
            onClick={() => {
              setShowConfirm(false);
            }}
          >
            Cancel
          </button>
          <button
            className="primaryButton ms-2"
            style={{ width: "6rem" }}
            onClick={() => {
              upgradePlanSelection();
            }}
          >
            Confirm{" "}
          </button>
        </div>
      </>
    );
  };

  return (
    <Box className="space-y-6">
      <ModelDialog
        open={showConfirm}
        handleClose={() => setShowConfirm(false)}
        dialogTitle=""
        dialogContent={<PlanConfirmationDialog />}
      />
      <Typography className="h3 mt-3">Current Plan Details</Typography>
      <div className="d-flex mt-3">
        <Typography className="body1 mb-2 col-4">Plan Name </Typography>
        <Typography className="h5 mb-1 ms-3" color="primary">
          : {get(userData, "subscription.subscriptionPlan", "-")}
        </Typography>
      </div>
      <div className="d-flex mt-2">
        <Typography className="body1 mb-2 col-4">Price </Typography>
        <Typography className="h5 mb-1 ms-3" color="primary">
          : $ {get(userData, "subscription.discPrice", "-")}
        </Typography>
      </div>
      <div className="d-flex mt-2">
        <Typography className="body1 mb-2 col-4">
          Next billing date:{" "}
        </Typography>
        <Typography className="h5 mb-1 ms-3" color="primary">
          : {moment(nextSubscriptionPlan?.startDate).format("ll")}
        </Typography>
      </div>

      <HiddenOn isHidden={!nextSubscriptionPlan?.subscriptionId}>
        <div>
          <Card
            className="col-12 mt-3 p-2"
            key="next-plan"
            style={{ background: "lightgreen" }}
          >
            <CardContent>
              <Typography className="h4 mb-1">
                <InfoOutlined fontSize="15" />
                Upcoming Plan Details
              </Typography>
              <div className="d-flex mt-2">
                <Typography className="body1 mb-1 col-4">Plan Name </Typography>
                <Typography className="h5 mb-1 ms-3" color="primary">
                  : {upcomingPlanFilter[0]?.subscriptionPlan}
                </Typography>
              </div>
              <div className="d-flex mt-2">
                <Typography className="body1 mb-2 col-4">Price </Typography>
                <Typography className="h5 mb-1 ms-3" color="primary">
                  : $ {upcomingPlanFilter[0]?.discPrice}
                </Typography>
              </div>
              <div className="d-flex mt-2">
                <Typography className="body1 mb-2 col-4">
                  Plan Start Date{" "}
                </Typography>
                <Typography className="h5 mb-4 ms-3" color="primary">
                  : {moment(nextSubscriptionPlan?.startDate).format("LL")}
                </Typography>
              </div>
            </CardContent>
          </Card>
        </div>
      </HiddenOn>

      <div className="col-12 mt-3 p-2">
        <Typography className="h4 mb-4">Available Plans</Typography>
        <Box className="pe-4">
          {filteredPlans.map((plan) => {
            return (
              <Card
                key={plan}
                className={`p-4 ${
                  isSamePlan(plan?.subscriptionId)
                    ? ""
                    : "border border-gray-200"
                } m-2 mt-2 mb-2 col-12`}
                style={
                  isSamePlan(plan?.subscriptionId)
                    ? { border: "1px solid #345DAE" }
                    : {}
                }
              >
                <RenderDataFields data={plan} fields={renderPlansData} />
                <div className="text-center">
                  <button
                    onClick={() => setPlanSelected(plan)}
                    className={
                      isSamePlan(plan?.subscriptionId)
                        ? "secondaryButton"
                        : "primaryButton"
                    }
                  >
                    {isSamePlan(plan?.subscriptionId)
                      ? "Plan Selected"
                      : "Select Plan"}
                  </button>
                </div>
              </Card>
            );
          })}
        </Box>
      </div>
      <div className="mt-3 mb-3">
        <button
          className={`primaryButton full-width`}
          color="primary"
          disabled={!planSelected.subscriptionId}
          style={planSelected.subscriptionId ? {} : { backgroundColor: "gray" }}
          onClick={() => {
            if (nextSubscriptionPlan.subscriptionId) {
              setShowConfirm(true);
            } else {
              upgradePlanSelection();
            }
          }}
        >
          Upgrade Plan
        </button>
      </div>
    </Box>
  );
};

export default MySubscriptions;
